import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Alert, Box, Button, createTheme, CssBaseline, ThemeProvider, Typography } from "@mui/material";
import { App } from "./App";
import { login, renewUser, userManager } from "./auth";
import { DiagnosticErrorBoundary } from "./DiagnosticErrorBoundary";
import i18n, { changeLanguage, normalizeLanguage } from "./i18n";

// A claimed service worker does not replace JavaScript already running in an
// installed Safari web app. Reload once when the new worker takes control so
// fixes become visible without requiring the operator to quit the Dock app.
if ("serviceWorker" in navigator) {
  let reloadingForUpdate = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (reloadingForUpdate) return;
    reloadingForUpdate = true;
    window.location.reload();
  });
}

const theme = createTheme({
  palette: {
    mode: "light",
    primary: { main: "#08264a", dark: "#041a35", light: "#244b72" },
    secondary: { main: "#17856f", dark: "#106452", light: "#4fae98" },
    warning: { main: "#e86f00", light: "#fff4e8" },
    error: { main: "#d92d2d", light: "#fff1f1" },
    background: { default: "#f5f7fa", paper: "#ffffff" },
    divider: "#dce3ea"
  },
  shape: { borderRadius: 8 },
  typography: {
    fontFamily: '"Inter", "Segoe UI", system-ui, sans-serif',
    h3: { fontSize: "clamp(2rem, 3vw, 2.75rem)", letterSpacing: "-.035em" },
    button: { fontWeight: 750 }
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: "#061f40",
          backgroundImage:
            "linear-gradient(120deg, rgba(79,132,181,.12), transparent 45%)",
          borderBottom: "1px solid rgba(255,255,255,.1)"
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          border: "1px solid #dce3ea",
          borderLeft: "4px solid #17856f",
          boxShadow: "0 2px 7px rgba(8,38,74,.06)",
          "@media (max-width: 599.95px)": {
            position: "relative",
            "& .mobile-card-expand": {
              width: 40,
              height: 40,
              alignSelf: "center",
              flexShrink: 0
            }
          }
        }
      }
    },
    MuiButton: {
      styleOverrides: { root: { minHeight: 38 } }
    }
  }
});

const queryClient = new QueryClient();

async function bootstrap() {
  if (window.location.pathname === "/oidc/callback") {
    try {
      const callbackUser = await userManager.signinRedirectCallback();
      const keycloakLanguage = normalizeLanguage(callbackUser.profile.locale);
      if (keycloakLanguage) {
        await changeLanguage(keycloakLanguage);
      }
    } catch (error) {
      console.error("OIDC callback failed", error);
      await userManager.removeUser();
    }
    window.history.replaceState({}, document.title, "/");
  }

  let oidcUser = await userManager.getUser();
  if (oidcUser?.expired) {
    oidcUser = await renewUser();
    if (!oidcUser) return;
  }

  if (!oidcUser) {
    try {
      await login(new URLSearchParams(window.location.search).get("sessionExpired") === "1");
      return;
    } catch (error) {
      console.error("OIDC redirect failed", error);
      ReactDOM.createRoot(document.getElementById("root")!).render(
        <React.StrictMode>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <Box sx={{ minHeight: "100dvh", display: "grid", placeItems: "center", p: 3 }}>
              <Box sx={{ width: "min(100%, 520px)" }}>
                <Box component="img" src="/brand/obviowms-header.svg"
                  alt="ObvioWMS — Simply Obvious"
                  sx={{ display: "block", width: 240, mb: 3, filter: "brightness(.25)" }} />
                <Alert severity="error" sx={{ mb: 2 }}>
                  {i18n.t("transverse.landing.authUnavailable")}
                </Alert>
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                  {i18n.t("transverse.landing.keycloakCheck")}
                </Typography>
                <Button variant="contained" onClick={() => window.location.reload()}>
                  {i18n.t("common.actions.retry")}
                </Button>
              </Box>
            </Box>
          </ThemeProvider>
        </React.StrictMode>
      );
      return;
    }
  }

  ReactDOM.createRoot(document.getElementById("root")!).render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <DiagnosticErrorBoundary>
            <App oidcUser={oidcUser} />
          </DiagnosticErrorBoundary>
        </ThemeProvider>
      </QueryClientProvider>
    </React.StrictMode>
  );

}

void bootstrap();
